import { LEAD_TYPE_ID, denormalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import { HIDDEN } from 'customer-data-objects/property/AccessLevel';
import { isBillingFrequency, isContactEmail, isCreateCloseDate, isCurrency, isDate, isDomain, isDuration, isEnum, isFile, isFileUpload, isGuidPropertyWithKnownGuid, isHtml, isMultiCurrencyCurrencyCode, isMultiCurrencyPrice, isNumber, isPercent, isPhoneNumber, isPipelineProperty, isPipelineStageProperty, isPriority, isProbability, isSubscriptionStatus, isPaymentStatus, isCheckbox, isMonthPeriod, isProductUrl, isTextarea, isDateTimeWithHiddenTime } from 'customer-data-objects/property/PropertyIdentifier';
import { RequirablePropertyInputCloseCreateDate, RequirablePropertyInputDate, RequirablePropertyInputDuration, RequirablePropertyInputEmailV2, RequirablePropertyInputEnum, RequirablePropertyInputExpandableText, RequirablePropertyInputFiles, RequirablePropertyInputHidden, RequirablePropertyInputHtml, RequirablePropertyInputKnownGuid, RequirablePropertyInputNumber, RequirablePropertyInputPaymentStatus, RequirablePropertyInputPercentage, RequirablePropertyInputPhoneWrapper, RequirablePropertyInputPipelineV2, RequirablePropertyInputPipelineStageV2, RequirablePropertyInputSettlementCurrency, RequirablePropertyInputPrice, RequirablePropertyInputPriority, RequirablePropertyInputReadOnly, RequirablePropertyInputSubscriptionStatus, RequirablePropertyInputCheckbox, RequirablePropertyInputMultiCurrencyCurrencyCodeV2, RequirablePropertyInputDealExchangeRateWrapper, RequirablePropertyInputMonthPeriod, RequirablePropertyInputUrl, RequirablePropertyInputFilesV2, RequirablePropertyInputDateTime, RequirablePropertyInputTextArea } from '../../input/PropertyInputGetters';
import BillingFrequencyPropertyInputEnumShim from '../shims/BillingFrequencyPropertyInputEnumShim';
import PropertyInputCurrencyShimV2 from '../shims/PropertyInputCurrencyShimV2';
import PropertyInputMultipleDomainShim from '../shims/PropertyInputMultipleDomainShim';
import PropertyInputMultipleEmailV2Shim from '../shims/PropertyInputMultipleEmailV2Shim';
import shouldDisplayCheckbox from '../../input/checkbox/utils/shouldDisplayCheckbox';
import PropertyInputExternalOptionShim from '../shims/PropertyInputExternalOptionShim';
import { getPropertyComponentResolver } from '../../resolvers/getPropertyComponentResolver';
import { isEditableDealExchangeRate, isRevenueEmail, isSettlementCurrencyProperty, shouldUseDateTimeInput } from './identifiers';
import { shouldShowPopovers } from './shouldShowPopovers';
import { FILE_TYPE_ENHANCED_UX_GATE } from '../../files/constants';
import { isDateTime } from 'customer-data-objects/property/PropertyIdentifier';
export const getPropertyInputComponent = ({
  accessLevel,
  enableMultipleEmailInput,
  enablePopovers,
  enableCheckboxInput,
  enablePhoneNumberInput,
  getAdditionalPropertyValue,
  getIsUngated,
  httpClient,
  objectId,
  objectTypeId,
  propertyDefinition,
  propertyValue,
  readOnly
}) => {
  const objectType = denormalizeTypeId(objectTypeId);

  // If the user doesn't have permissions to view the property we always show
  // the hidden
  if (accessLevel === HIDDEN) {
    return RequirablePropertyInputHidden;
  }
  const resolver = getPropertyComponentResolver({
    getAdditionalPropertyValue,
    getIsUngated,
    property: propertyDefinition,
    objectId,
    objectTypeId,
    httpClient,
    value: propertyValue
  });

  //////////////////// START OF RESOLVER INPUTS ////////////////////////////////

  // uses RRL
  if (isPipelineProperty(propertyDefinition, objectTypeId)) {
    return RequirablePropertyInputPipelineV2;
  }

  // uses RRL
  if (isPipelineStageProperty(propertyDefinition, objectTypeId)) {
    return RequirablePropertyInputPipelineStageV2;
  }
  if (isMultiCurrencyCurrencyCode(propertyDefinition, objectType)) {
    return RequirablePropertyInputMultiCurrencyCurrencyCodeV2;
  }
  if (isFile(propertyDefinition) && getIsUngated(FILE_TYPE_ENHANCED_UX_GATE)) {
    return RequirablePropertyInputFilesV2;
  }

  // If we don't have a custom input, default to a standard
  // reference-resolvers-lite select
  if (resolver) {
    return PropertyInputExternalOptionShim;
  }

  //////////////////// END OF RESOLVER INPUTS //////////////////////////////////

  if (shouldShowPopovers({
    enablePopovers,
    objectId
  }) && isDomain(propertyDefinition, objectType)) {
    return PropertyInputMultipleDomainShim;
  }
  if (
  // multi-email input requires extra `actions` (API callbacks) that most
  // caller callers don't provide. it requires explicit opt-in via
  // `enableMultipleEmailInput` even when popovers are enabled
  enableMultipleEmailInput && shouldShowPopovers({
    enablePopovers,
    objectId
  }) && isContactEmail(propertyDefinition, objectType)) {
    return PropertyInputMultipleEmailV2Shim;
  }
  if (isContactEmail(propertyDefinition, objectType)) {
    return RequirablePropertyInputEmailV2;
  }
  if (isRevenueEmail(propertyDefinition, objectType)) {
    return RequirablePropertyInputEmailV2;
  }

  // Percent properties are number properties so need to preempt it
  if (isPercent(propertyDefinition) || isProbability(propertyDefinition)) {
    return RequirablePropertyInputPercentage;
  }

  // Duration properties are number properties so need to preempt it
  if (isDuration(propertyDefinition)) {
    return RequirablePropertyInputDuration;
  }
  if (isSettlementCurrencyProperty(propertyDefinition, objectTypeId)) {
    return RequirablePropertyInputSettlementCurrency;
  }
  if (isMultiCurrencyPrice(propertyDefinition)) {
    return RequirablePropertyInputPrice;
  }
  if (isCurrency(propertyDefinition)) {
    return PropertyInputCurrencyShimV2;
  }
  if (isMonthPeriod(propertyDefinition, objectTypeId)) {
    return RequirablePropertyInputMonthPeriod;
  }

  // Html properties render their own readonlys (for previewing in the RTE)
  if (isHtml(propertyDefinition)) {
    return RequirablePropertyInputHtml;
  }

  // NOTE: even though phone number input is a popover, it is not affected
  //       by the `enablePopovers` flag because "simple" forms such as the
  //       record creator have historically shown it. callers can opt-out
  //       separately via `enablePhoneNumberInput`
  if (enablePhoneNumberInput && isPhoneNumber(propertyDefinition)) {
    return RequirablePropertyInputPhoneWrapper;
  }
  if (isEditableDealExchangeRate(propertyDefinition, objectTypeId)) {
    return RequirablePropertyInputDealExchangeRateWrapper;
  }

  // numbers can render their own readonlys
  if (isNumber(propertyDefinition)) {
    return RequirablePropertyInputNumber;
  }

  // TODO: condition block to be removed when all users are ungated
  if (objectTypeId === LEAD_TYPE_ID && propertyDefinition.name === 'hs_lead_label') {
    if (getIsUngated('Leads:Tags')) {
      return RequirablePropertyInputPriority;
    } else {
      return RequirablePropertyInputEnum;
    }
  }
  if (isPriority(propertyDefinition)) {
    return RequirablePropertyInputPriority;
  }
  if (isGuidPropertyWithKnownGuid(propertyDefinition, objectType, propertyValue)) {
    return RequirablePropertyInputKnownGuid;
  }
  if (isSubscriptionStatus(propertyDefinition, objectType)) {
    return RequirablePropertyInputSubscriptionStatus;
  }
  if (isPaymentStatus(propertyDefinition, objectType)) {
    return RequirablePropertyInputPaymentStatus;
  }
  if (isCheckbox(propertyDefinition) && shouldDisplayCheckbox(propertyDefinition, objectType, enableCheckboxInput)) {
    return RequirablePropertyInputCheckbox;
  }
  if (isProductUrl(propertyDefinition, objectTypeId)) {
    return RequirablePropertyInputUrl;
  }
  if (readOnly) {
    if (isFileUpload(propertyDefinition)) {
      return RequirablePropertyInputFiles;
    }
    return RequirablePropertyInputReadOnly;
  }

  // specifically after 'read only' so that PropertyInputReadOnly will display
  // comma-separated lists instead of a disabled line of multi-select pills
  if (isEnum(propertyDefinition)) {
    if (isBillingFrequency(propertyDefinition, objectTypeId)) {
      return BillingFrequencyPropertyInputEnumShim;
    }
    return RequirablePropertyInputEnum;
  }

  // specifically after 'read only' so that PropertyInputReadOnly will display
  // formatted date and datetime strings in readonly contexts
  if (isCreateCloseDate(propertyDefinition, objectType)) {
    return RequirablePropertyInputCloseCreateDate;
  }

  // specifically after 'read only' so that PropertyInputReadOnly will display
  // formatted datetime strings in readonly contexts
  if (getIsUngated('CRM:PropertiesFrontend:DatetimeProperty')) {
    if (isDateTime(propertyDefinition)) {
      return isDateTimeWithHiddenTime(propertyDefinition, objectTypeId) ? RequirablePropertyInputDate : RequirablePropertyInputDateTime;
    }
  } else {
    if (shouldUseDateTimeInput(propertyDefinition, objectTypeId)) {
      return RequirablePropertyInputDateTime;
    }
  }

  // specifically after 'read only' so that PropertyInputReadOnly will display
  // formatted date and datetime strings in readonly contexts
  if (isDate(propertyDefinition)) {
    return RequirablePropertyInputDate;
  }
  if (isTextarea(propertyDefinition)) {
    return RequirablePropertyInputTextArea;
  }

  // anything else defaults to `text` through the expandable text input
  return RequirablePropertyInputExpandableText;
};